import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import axios from "axios";
import { bindActionCreators } from "redux";
import { useDispatch } from "react-redux";
import { actionCreators } from "../state/index";
import { connect } from "react-redux";
import { logout } from "../state/action-creator/index";
import { API_ENDPOINTS } from "../config/api";
import useTranslation from "../hooks/useTranslation";

import NavTab from "../components/NavTab";
import Button from "../components/Button";
import { SmallAvatar } from "../components/Avatar";
import { Container } from "../layout/Container";
import { HeaderSection } from "../layout/CardsContainer";
import {
  FieldGroup,
  InputGroup,
  InputPassword,
  TextareaGroup,
  Row,
} from "../components/Input";
import {
  validateEmail,
  validUsername,
  validPassword,
  checkComfirmPassword,
} from "../utils.js";

const Settings = ({ logout }) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(1);
  const user = useSelector((state) => state.user?.data);

  // Hide "change password" for Google OAuth (password managed by Google)
  const getSocialLinks = (u) => {
    const v = u?.social_links ?? u?.socialLinks;
    if (!v) return null;
    if (typeof v === "string") {
      try {
        return JSON.parse(v);
      } catch (_) {
        return null;
      }
    }
    return v;
  };

  const socialLinks = getSocialLinks(user);
  const isGoogleOAuth = !!socialLinks?.google;

  const tabs = isGoogleOAuth
    ? ["edit_profile", "channel_settings", "multistreaming"]
    : ["edit_profile", "change_password", "channel_settings", "multistreaming"];

  const list = tabs.map((k) => t(`settings.${k}`));
  const activeTab = tabs[index - 1] || tabs[0];

  return (
    <Container
      style={{ maxWidth: "600px", marginTop: "3rem", marginBottom: "3rem" }}
    >
      <HeaderSection title={t('settings.profile_settings')} />
      <div style={{ position: "relative" }}>
        <NavTab list={list} index={index} setIndex={setIndex} />
        {activeTab === "edit_profile" && <EditProfile />}
        {activeTab === "change_password" && <ChangePassword />}
        {activeTab === "channel_settings" && <ChannelSettings />}
        {activeTab === "multistreaming" && <MultiStreamingSettings />}
      </div>
    </Container>
  );
};
export default Settings;

const MultiStreamingSettings = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState({
    enabled: false,
    twitchEnabled: false,
    twitchKey: '',
    twitchSaved: false,
    youtubeEnabled: false,
    youtubeKey: '',
    youtubeSaved: false,
    facebookEnabled: false,
    facebookKey: '',
    facebookSaved: false
  });

  useEffect(() => {
    setLoading(true);
    axios.get('/api/stream/restream/config', { withCredentials: true })
      .then(res => {
        const data = res.data || {};
        // Show masked state but avoid resending masked string back
        setCfg(prev => ({
          ...prev,
          enabled: !!data.enabled,
          twitchEnabled: !!data.twitchEnabled,
          youtubeEnabled: !!data.youtubeEnabled,
          facebookEnabled: !!data.facebookEnabled,
          twitchSaved: !!(data.twitchKey && String(data.twitchKey).length > 0),
          youtubeSaved: !!(data.youtubeKey && String(data.youtubeKey).length > 0),
          facebookSaved: !!(data.facebookKey && String(data.facebookKey).length > 0),
          twitchKey: '',
          youtubeKey: '',
          facebookKey: ''
        }));
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setCfg(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const save = () => {
    setLoading(true);
    // Only send keys if user provided a non-empty value
    const payload = {
      enabled: cfg.enabled,
      twitchEnabled: cfg.twitchEnabled,
      youtubeEnabled: cfg.youtubeEnabled,
      facebookEnabled: cfg.facebookEnabled
    };
    if (cfg.twitchKey && cfg.twitchKey.trim() !== '') payload.twitchKey = cfg.twitchKey.trim();
    if (cfg.youtubeKey && cfg.youtubeKey.trim() !== '') payload.youtubeKey = cfg.youtubeKey.trim();
    if (cfg.facebookKey && cfg.facebookKey.trim() !== '') payload.facebookKey = cfg.facebookKey.trim();

    axios.put('/api/stream/restream/config', payload, { withCredentials: true })
      .then(() => toast.success('Multi‑stream configuration saved'))
      .catch(() => toast.error('Failed to save configuration'))
      .finally(() => setLoading(false));
  };

  return (
    <Form onSubmit={(e) => { e.preventDefault(); save(); }}>
      <Row>
        <FieldGroup label={t('settings.enable_multistreaming')}>
          <label>
            <input type="checkbox" name="enabled" checked={cfg.enabled} onChange={handleChange} />
            <span className="switch-label">{t('settings.enable')}</span>
          </label>
        </FieldGroup>
      </Row>
      <Row>
        <FieldGroup label={t('settings.twitch')}>
          <label>
            <input type="checkbox" name="twitchEnabled" checked={cfg.twitchEnabled} onChange={handleChange} />
            <span className="switch-label">{t('settings.enable')}</span>
          </label>
          {cfg.twitchSaved && <SavedBadge>{t('settings.saved')}</SavedBadge>}
          <InputGroup name="twitchKey" placeholder={t('settings.a_key_is')} type="text" value={cfg.twitchKey || ''} onChange={handleChange} />
          {cfg.twitchSaved && <p className="field-hint">{t('settings.a_key_is')}</p>}
        </FieldGroup>
        <FieldGroup label={t('settings.youtube')}>
          <label>
            <input type="checkbox" name="youtubeEnabled" checked={cfg.youtubeEnabled} onChange={handleChange} />
            <span className="switch-label">{t('settings.enable')}</span>
          </label>
          {cfg.youtubeSaved && <SavedBadge>{t('settings.saved')}</SavedBadge>}
          <InputGroup name="youtubeKey" placeholder={t('settings.a_key_is')} type="text" value={cfg.youtubeKey || ''} onChange={handleChange} />
          {cfg.youtubeSaved && <p className="field-hint">{t('settings.a_key_is')}</p>}
        </FieldGroup>
      </Row>
      <Row>
        <FieldGroup label={t('settings.facebook')}>
          <label>
            <input type="checkbox" name="facebookEnabled" checked={cfg.facebookEnabled} onChange={handleChange} />
            <span className="switch-label">{t('settings.enable')}</span>
          </label>
          {cfg.facebookSaved && <SavedBadge>{t('settings.saved')}</SavedBadge>}
          <InputGroup name="facebookKey" placeholder={t('settings.a_key_is')} type="text" value={cfg.facebookKey || ''} onChange={handleChange} />
          {cfg.facebookSaved && <p className="field-hint">{t('settings.a_key_is')}</p>}
        </FieldGroup>
      </Row>
      <p>{t('settings.keep_streaming_to')}</p>
      <Button variant="primary" type="submit" disabled={loading}>{t('settings.save')}</Button>
    </Form>
  );
};

const EditProfile = () => {
  const { t } = useTranslation();
  const oldData = useSelector((state) => state.user?.data);
  const [data, setData] = useState(oldData);
  const dispatch = useDispatch();
  const { login } = bindActionCreators(actionCreators, dispatch);
  const [loginData, setLoginData] = useState(null);

  const [save, setSave] = useState(false);
  const initFields = {
    firstName: false,
    lastName: false,
    username: false,
    email: false,
  };
  const [changed, setChanged] = useState(initFields);
  const [errors, setErrors] = useState({
    username: "",
    email: "",
  });

  const handleChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
    // get changed inputs
    setChanged({ ...changed, [e.target.name]: true });
  };
  function formValid() {
    setErrors({
      ...errors,
      username: validUsername(data?.username),
      email: validateEmail(data?.email),
    });
    if (
      validUsername(data?.username).length === 0 &&
      validateEmail(data?.email).length === 0
    )
      return true;
    return false;
  }

  function postData(field, data) {
    let url;
    switch (field) {
      case "fullname":
        url = API_ENDPOINTS.UPDATE_FULLNAME;
        break;
      case "password":
        url = API_ENDPOINTS.UPDATE_PASSWORD;
        break;
      default:
        url = API_ENDPOINTS.CHANNELS_SETTINGS_UPDATE + field;
    }

    axios
      .put(url, data, {
        withCredentials: true,
      })
      .then((res) => {
        toast.success(res.data?.msg);
        setLoginData({ ...oldData, ...data });
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || err.response?.data?.msg);
      });
  }

  useEffect(() => {
    if (loginData) login(loginData);
  }, [loginData, login]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if ((changed.email || changed.username) && !formValid()) return;
    setChanged(initFields);

    // Mise à jour du nom complet
    if (changed.firstName || changed.lastName) {
      postData("fullname", {
        firstName: data?.firstName,
        lastName: data?.lastName,
      });
    }

    // Mise à jour du nom d'utilisateur
    if (changed.username) {
      axios
        .put(
          API_ENDPOINTS.UPDATE_USERNAME,
          { username: data?.username },
          {
            withCredentials: true,
          }
        )
        .then((res) => {
          toast.success(res.data?.msg);
          setLoginData({ ...oldData, username: data?.username });
        })
        .catch((err) => {
          setErrors({ username: err.response?.data?.msg });
        });
    }
    setErrors({});
  };
  return (
    <>
      <ChangeAvatar username={data?.username} fullName={data?.username} />
      <Form onSubmit={handleSubmit} onChange={() => setSave(true)}>
        <Row>
          <FieldGroup label={t('settings.first_name')}>
            <InputGroup
              name="firstName"
              placeholder={t('settings.enter_your_first')}
              type="text"
              value={data?.firstName}
              onChange={handleChange}
              alert={errors.firstName}
            />
          </FieldGroup>
          <FieldGroup label={t('settings.last_name')}>
            <InputGroup
              name="lastName"
              placeholder={t('settings.enter_your_last')}
              type="text"
              value={data?.lastName}
              onChange={handleChange}
              alert={errors.lastName}
            />
          </FieldGroup>
        </Row>
        <FieldGroup label={t('settings.username') + " *"}>
          <InputGroup
            name="username"
            placeholder={t('settings.enter_your_username')}
            type="text"
            value={data?.username}
            onChange={handleChange}
            alert={errors.username}
          />
        </FieldGroup>
        <FieldGroup label="Email *">
          <InputGroup
            name="email"
            placeholder={t('settings.enter_your_email')}
            type="text"
            value={data?.email}
            onChange={handleChange}
            alert={errors.email}
            disabled
          />
        </FieldGroup>
        <Button variant={save && "primary"} disabled={!save} type="submit">
          {t('settings.save')} Changes
        </Button>
      </Form>
    </>
  );
};
const ChangePassword = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { login } = bindActionCreators(actionCreators, dispatch);
  const [data, setData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  function formValid() {
    setErrors({
      oldPassword: validPassword(data?.oldPassword),
      newPassword: validPassword(data?.newPassword),
      confirmPassword: checkComfirmPassword(
        data?.newPassword,
        data?.confirmPassword
      ),
    });
    if (
      validPassword(data?.oldPassword).length === 0 &&
      validPassword(data?.newPassword).length === 0 &&
      checkComfirmPassword(data?.newPassword, data?.confirmPassword).length ===
      0
    )
      return true;
    return false;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formValid()) return;

    axios
      .put(
        API_ENDPOINTS.UPDATE_PASSWORD,
        { oldPassword: data?.oldPassword, newPassword: data?.newPassword },
        {
          withCredentials: true,
        }
      )
      .then((res) => {
        toast.success(res.data?.msg);
        // After password change, re-fetch user to refresh avatar URL in Redux
        axios
          .get(API_ENDPOINTS.AUTH_ME, { withCredentials: true })
          .then((me) => {
            const freshUser = me?.data?.user || me?.data;
            if (freshUser) {
              const serverUrl = freshUser.avatar;
              const cacheBusted = serverUrl ? `${serverUrl}${serverUrl.includes("?") ? "&" : "?"}t=${Date.now()}` : serverUrl;
              login({ ...freshUser, avatar: cacheBusted });
            }
          })
          .catch(() => { })
          .finally(() => { });
      })
      .catch((err) => {
        if (err.response?.data?.message?.includes("Wrong password"))
          setErrors({ oldPassword: "Wrong password" });
        else if (err.response?.data?.statusCode === 400)
          setErrors({
            oldPassword: "old password and new password are the same",
            newPassword: "old password and new password are the same",
          });
        else toast.error(err.response?.data?.message || err.response?.data?.msg);
      });
  };
  const handleChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FieldGroup label={t('settings.old_password')}>
        <InputPassword
          name="oldPassword"
          placeholder={t('settings.old_password')}
          onChange={handleChange}
          alert={errors.oldPassword}
        />
      </FieldGroup>
      <FieldGroup label={t('settings.new_password')}>
        <InputPassword
          name="newPassword"
          placeholder={t('settings.new_password')}
          onChange={handleChange}
          alert={errors.newPassword}
        />
      </FieldGroup>
      <FieldGroup label={t('settings.confirm_password')}>
        <InputPassword
          name="confirmPassword"
          placeholder={t('settings.confirm_password')}
          onChange={handleChange}
          alert={errors.confirmPassword}
        />
      </FieldGroup>
      <Button
        style={{ marginTop: "20px", marginBottom: "24px" }}
        variant="primary"
        type="submit"
      >
        {t('settings.save')} Changes
      </Button>
      <Link to="/reset-password">{t('settings.forgot_password')}</Link>
    </Form>
  );
};

function getError(errors, key) {
  for (let i = 0; i < errors?.length; i++)
    if (errors[i].param === key) return true;
  return false;
}

const ChannelSettings = () => {
  const { t } = useTranslation();
  const [save, setSave] = useState(false);
  const [social, setSocial] = useState({});
  const username = useSelector((state) => state.user?.data?.username);
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({
    donation: "",
    website: "",
    youtube: "",
    facebook: "",
    instagram: "",
    twitter: "",
  });

  useEffect(() => {
    if (username) {
      axios
        // Utiliser l'endpoint "mes infos de chaîne" (auth + enforceChannel),
        // plutôt que l'endpoint public /profile/:username.
        .get(API_ENDPOINTS.CHANNELS_SETTINGS, {
          withCredentials: true,
        })
        .then((res) => {
          const prof = res.data || {};
          setData({
            about: prof?.about || '',
            paypalme: prof?.paypalme || '',
          });
          setSocial({ ...(prof?.social || {}) });
        })
        .catch((err) => {
          if (err?.response?.status === 401) logout();
          // Premier accès possible: pas encore de config → ne pas afficher d'erreur bloquante
          else if (err?.response?.status === 404) {
            setData({ about: '', paypalme: '' });
            setSocial({});
          } else {
            toast.error(err?.response?.data?.msg || 'Failed to load channel settings');
          }
        });
    }
  }, [username]);

  const handleSubmit = (e) => {
    e.preventDefault();
    axios
      .put(
        API_ENDPOINTS.CHANNELS_SETTINGS,
        { ...data, ...social },
        {
          withCredentials: true,
        }
      )
      .then(() => {
        toast.success("Channel settings are saved");
      })
      .catch((err) => {
        setErrors({
          donation: getError(err?.response?.data?.errors, "paypalme")
            ? "You have to enter only your paypal.me username"
            : "",
          website: getError(err?.response?.data?.errors, "website")
            ? "You have to enter your website URL"
            : "",
          youtube: getError(err?.response?.data?.errors, "youtube")
            ? "You have to enter only your chanel username"
            : "",
          facebook: getError(err?.response?.data?.errors, "facebook")
            ? "You have to enter only your facebook username"
            : "",
          instagram: getError(err?.response?.data?.errors, "instagram")
            ? "You have to enter only your instagram username"
            : "",
          twitter: getError(err?.response?.data?.errors, "twitter")
            ? "You have to enter only your twitter username"
            : "",
        });
      });
    setErrors({});
  };
  const handleChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };
  const handleSocial = (e) => {
    if (e.target.name === "website" && e.target.value === "")
      setSocial({ ...data, website: null });
    else setSocial({ ...social, [e.target.name]: e.target.value });
  };
  return (
    <Form onSubmit={handleSubmit} onChange={() => setSave(true)}>
      <FieldGroup label={t('settings.paypal')}>
        <InputGroup
          name="paypalme"
          placeholder={t('settings.your_paypalme_url')}
          type="text"
          value={data?.paypalme || ""}
          onChange={handleChange}
          alert={errors.donation}
        />
        <p>{t('settings.add_your_paypalme')}</p>
      </FieldGroup>
      <FieldGroup label={t('settings.website')}>
        <InputGroup
          name="website"
          placeholder={t('settings.your_website_url')}
          type="text"
          value={social?.website || ""}
          onChange={handleSocial}
          alert={errors.website}
        />
      </FieldGroup>
      <Row>
        <FieldGroup label={t('settings.youtube')}>
          <InputGroup
            name="youtube"
            placeholder={t('settings.your_youtube_channel')}
            type="text"
            value={social?.youtube || ""}
            onChange={handleSocial}
            alert={errors.youtube}
          />
        </FieldGroup>
        <FieldGroup label={t('settings.facebook')}>
          <InputGroup
            name="facebook"
            placeholder={t('settings.your_facebooke_url')}
            type="text"
            value={social?.facebook || ""}
            onChange={handleSocial}
            alert={errors.facebook}
          />
        </FieldGroup>
      </Row>
      <Row>
        <FieldGroup label={t('settings.instagram')}>
          <InputGroup
            name="instagram"
            placeholder={t('settings.your_instagram_url')}
            type="text"
            value={social?.instagram || ""}
            onChange={handleSocial}
            alert={errors.instagram}
          />
        </FieldGroup>
        <FieldGroup label={t('settings.twitter')}>
          <InputGroup
            name="twitter"
            placeholder={t('settings.your_twitter_url')}
            type="text"
            value={social?.twitter || ""}
            onChange={handleSocial}
            alert={errors.twitter}
          />
        </FieldGroup>
      </Row>
      <FieldGroup label="Bio">
        <TextareaGroup
          name="about"
          placeholder={t('settings.your_biographie')}
          value={data?.about || ""}
          onChange={handleChange}
        />
      </FieldGroup>
      <Button variant={save && "primary"} disabled={!save} type="submit">
        {t('settings.save')} Changes
      </Button>
    </Form>
  );
};

const ChangeAvatar = ({ username, fullName }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { login } = bindActionCreators(actionCreators, dispatch);
  const store = useSelector((state) => state.user?.data);
  const [avatarPrev, setAvatarPrev] = useState(store?.avatar);
  // Keep local preview in sync with Redux store updates (e.g., changed from Profile page)
  useEffect(() => {
    setAvatarPrev(store?.avatar);
  }, [store?.avatar]);

  const [data, setData] = useState(null);

  const addCacheBuster = (url) => {
    if (!url) return url;
    const s = String(url);
    return `${s}${s.includes("?") ? "&" : "?"}t=${Date.now()}`;
  };

  const refreshMeAndSync = async () => {
    try {
      const me = await axios.get(API_ENDPOINTS.AUTH_ME, { withCredentials: true });
      const freshUser = me?.data?.user || me?.data;
      if (!freshUser) return null;
      const synced = { ...freshUser, avatar: addCacheBuster(freshUser.avatar) };
      login(synced);
      setAvatarPrev(synced.avatar);
      return synced;
    } catch (_) {
      return null;
    }
  };

  const handleChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("filename", file);
    axios
      .post(API_ENDPOINTS.AVATAR, formData, {
        withCredentials: true,
        headers: { "Content-type": "multipart/form-data" },
      })
      .then((res) => {
        const serverUrl = res?.data?.newAvatar || res?.data?.avatar;
        if (serverUrl) {
          const cacheBusted = addCacheBuster(serverUrl);
          setAvatarPrev(cacheBusted);
          setData({ ...store, avatar: cacheBusted });
        } else {
          // Prefer authoritative refresh from /me (handles APIs that don't return the new URL)
          refreshMeAndSync().then((synced) => {
            if (!synced) {
              // Final fallback to local preview (not stored in Redux)
              const localPreview = URL.createObjectURL(file);
              setAvatarPrev(localPreview);
            }
          });
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.errors?.[0]?.msg || "Failed to upload avatar");
      });
  };

  useEffect(() => {
    if (data) login(data);
  }, [data, login]);

  return (
    <ChangeAvatarStyle>
      <label htmlFor="avatar">
        <SmallAvatar size="70px" src={avatarPrev || store?.avatar} alt={fullName} />
      </label>
      <div>
        <h5>{username}</h5>
        <label htmlFor="avatar">{t('settings.change_profile_picture')}</label>
        <input
          onChange={handleChange}
          name="avatar"
          id="avatar"
          type="file"
          accept="image/*"
        />
      </div>
    </ChangeAvatarStyle>
  );
};

const ChangeAvatarStyle = styled.form`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  > div:nth-child(2) {
    margin-left: 18px;
  }
  label {
    cursor: pointer;
    font-family: "Poppins Medium";
    font-size: 12px;
    color: ${({ theme }) => theme.color.primary};
    letter-spacing: 0.3px;
  }
  input {
    display: none;
  }
`;
const Form = styled.form`
  & > button {
    width: 100%;
  }
  p {
    font-family: "Poppins Medium";
    font-size: 12px;
    color: ${({ theme }) => theme.color.lighterText};
    margin-bottom: 24px;
    margin-top: 10px;
  }
  .field-hint {
    margin-top: 6px;
    margin-bottom: 0;
  }
  .switch-label {
    font-family: "Poppins Medium";
    font-size: 12px;
    color: ${({ theme }) => theme.color.lighterText};
    margin-left: 10px;
    position: relative;
    bottom: 3px;
    text-transform: initial;
  }
`;

const SavedBadge = styled.span`
  display: inline-block;
  margin-left: 10px;
  padding: 2px 6px;
  font-size: 11px;
  border-radius: 6px;
  background: ${({ theme }) => theme.color.success}22;
  color: ${({ theme }) => theme.color.success};
  vertical-align: middle;
`;

connect({ logout })(Settings);
